class HeaderMenu extends HTMLElement {
  static observedAttributes = ['data-sticky'];
  constructor() {
    super();
    this.placeMegamenus();
    this.interactionMode = this.dataset.interactionMode;
    this.currentInteractionMode = this.dataset.interactionMode;
    this.elements = {
      menuLinks: this.querySelectorAll('a'),
      triggers: this.querySelectorAll('[data-menu-trigger][aria-expanded]'),
      megamenus: this.querySelectorAll('[data-submenu="megamenu"]'),
      submenus: this.querySelectorAll('[data-submenu]'),
      secondaryMenu: this.querySelector('[data-secondary-menu]'),
      background: this.querySelector('.header__background'),
    };
    this.returnColorScheme = [];

    this.mobileToggle = this.querySelector('[data-mobile-menu-toggle]');
    this.mobileMenu = this.mobileToggle
      ? document.getElementById(this.mobileToggle.getAttribute('aria-controls'))
      : null;
    if (this.mobileToggle && this.mobileMenu) {
      this.mobileToggle.addEventListener('click', () => {
        if (this.mobileToggle.getAttribute('aria-expanded') === 'true') {
          this.closeMobileMenu();
        } else {
          this.openMobileMenu();
        }
      });
      this.mobileMenu.querySelectorAll('[data-mobile-accordion]').forEach((button) => {
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', !expanded);
        });
      });
      // A drawer opened from inside the menu (e.g. Wishlist) replaces it.
      this.mobileMenu.addEventListener('click', (event) => {
        if (event.target.closest('dialog-trigger, [data-shipping-to-open]')) this.closeMobileMenu();
      });
      document.addEventListener('keydown', (event) => {
        if (event.code === 'Escape' && this.mobileToggle.getAttribute('aria-expanded') === 'true') {
          this.closeMobileMenu();
        }
      });
      window.matchMedia('(min-width: 64rem)').addEventListener('change', (event) => {
        if (event.matches) this.closeMobileMenu();
      });
    }

    this.elements.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (this.currentInteractionMode === 'hover' && Date.now() - 500 < this.timeOpened) {
          return (this.currentInteractionMode = 'click');
        } else {
          this.toggleTrigger(event);
          this.currentInteractionMode = 'click';
        }
      });

      trigger.addEventListener('mouseover', (event) => {
        if (this.interactionMode === 'hover') {
          const nextSubmenu = document.getElementById(trigger.getAttribute('aria-controls'));
          if (nextSubmenu) this.flagIfSwitching(nextSubmenu);
          const openMenus = this.querySelectorAll('[data-menu-trigger][aria-expanded="true"]');
          openMenus.forEach((openMenu) => {
            const submenu = document.getElementById(openMenu.getAttribute('aria-controls'));
            // Containment, not identity: mouseover targets spans inside the trigger, and
            // closing the hovered menu would restart its animation on every move.
            if (openMenu && !submenu.contains(event.target) && submenu !== event.target && !openMenu.contains(event.target)) {
              this.closeSubmenu(submenu, true);
            }
          });
          const submenu = document.getElementById(trigger.getAttribute('aria-controls'));
          this.openSubmenu(submenu);
          this.currentInteractionMode = 'hover';
          document.addEventListener(
            'keydown',
            (event) => {
              if (event.code.toUpperCase() === 'ESCAPE') this.closeSubmenu(submenu);
            },
            { once: true }
          );
          this.addEventListener(
            'mouseleave',
            () => this.currentInteractionMode === 'hover' && this.closeSubmenu(submenu, true),
            { once: true }
          );
        }
      });
    });

    if (this.interactionMode === 'hover') {
      this.elements.menuLinks.forEach((trigger) => {
        trigger.addEventListener('mouseenter', (event) => {
          if (this.currentInteractionMode !== 'hover') return;
          const openMenus = this.querySelectorAll('[data-menu-trigger][aria-expanded="true"]');
          openMenus.forEach((openMenu) => {
            const submenu = document.getElementById(openMenu.getAttribute('aria-controls'));
            if (openMenu && !submenu.contains(event.target) && submenu !== event.target && event.target !== openMenu) {
              this.closeSubmenu(submenu, true);
            }
          });
        });
      });
    }

    this.elements.submenus.forEach((submenu) => {
      submenu.addEventListener('keydown', (event) => this.onSubmenuKeyDown(event));
      submenu.addEventListener('focusout', (event) => this.onSubmenuFocusOut(event, submenu));
    });

    // Must match the :has() transparent-header gate in src/tailwind.css.
    this.transparentSection = document.querySelector(
      'main .shopify-section:first-child [data-support-transparent-header]'
    );
    this.isTransparent = !!this.transparentSection;
    this.classList.toggle('header--transparent', this.isTransparent);

    this.headerObserver = new ResizeObserver((entries) => {
      if (window.matchMedia('(max-width:71.99em)').matches) {
        this.elements.submenus.forEach((submenu) => {
          this.closeSubmenu(submenu, true);
        });
      }
      const height = Math.floor(this.clientHeight) - 1;
      if (this.classList.contains('header--transparent')) {
        document.documentElement.style.setProperty('--transparent-header-height', height + 'px');
      } else {
        document.documentElement.style.setProperty('--transparent-header-height', '0px');
      }
    });
    this.headerObserver.observe(this);

    // Panels are clipped to --megamenu-height (src/tailwind.css); observing the open
    // panel lets the backdrop morph between menus and track late-loading images.
    this.sheetObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = Math.ceil(entry.borderBoxSize[0].blockSize);
        this.style.setProperty('--megamenu-height', height + 'px');
      }
    });

    this.megamenuObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const height = entry.borderBoxSize[0].blockSize;
        if (entry.target.getAttribute('data-submenu') === 'nested-dropdown') {
          if (height > this.submenuHeight) {
            this.setSubmenuHeight(height);
          }
        } else {
          this.setSubmenuHeight(height);
        }
      }
    });

    document.addEventListener('click', (event) => {
      this.currentInteractionMode == 'click';
      const openMenus = this.querySelectorAll('[data-menu-trigger][aria-expanded="true"]');
      openMenus.forEach((openMenu) => {
        const submenu = document.getElementById(openMenu.getAttribute('aria-controls'));

        if (openMenu && !submenu.contains(event.target) && submenu != event.target && !openMenu.contains(event.target)) {
          this.closeSubmenu(submenu, true);
        }
      });
    });
  }

  flagIfSwitching(nextSubmenu) {
    const openMegamenu = this.querySelector('[aria-expanded="true"] + [data-submenu="megamenu"]');
    if (!openMegamenu || openMegamenu === nextSubmenu) return;
    if (nextSubmenu.getAttribute('data-submenu') !== 'megamenu') return;
    this.setAttribute('data-switching', '');
    clearTimeout(this.switchTimer);
    this.switchTimer = setTimeout(() => this.removeAttribute('data-switching'), 400);
  }

  openMobileMenu() {
    this.mobileToggle.setAttribute('aria-expanded', 'true');
    // The menu hangs off the bar's bottom edge, which sits lower while the
    // announcement bar is in view; size it to what's left of the viewport.
    this.style.setProperty('--mobile-menu-top', `${Math.max(0, this.getBoundingClientRect().bottom)}px`);
    this.setAttribute('open', 'mobile');
    const scheme = this.mobileMenu.getAttribute('data-color-scheme-transition');
    if (scheme) {
      this.setAttribute('data-color-scheme', scheme);
      this.mobileMenu.setAttribute('data-color-scheme', scheme);
    }
    document.body.style.overflow = 'hidden';
  }

  closeMobileMenu() {
    if (!this.mobileToggle) return;
    this.mobileToggle.setAttribute('aria-expanded', 'false');
    if (this.getAttribute('open') === 'mobile') this.removeAttribute('open');
    const original = this.mobileMenu.getAttribute('data-color-scheme-original');
    if (original) {
      this.setAttribute('data-color-scheme', original);
      this.mobileMenu.setAttribute('data-color-scheme', original);
    }
    document.body.style.overflow = '';
  }

  closeSubmenu(submenu, preventFocus) {
    this.classList.remove('header--alt-logo');
    this.removeAttribute('open');
    const trigger = this.querySelector(`[aria-controls="${submenu.getAttribute('id')}"]`);
    trigger.setAttribute('aria-expanded', false);

    if (!this.querySelector('[aria-expanded="true"]')) {
      this.classList.remove('header--background');
      this.style.setProperty('--megamenu-height', '0px');
    }

    this.elements.secondaryMenu?.classList.remove('header__right--inactive');
    this.megamenuObserver.unobserve(submenu);
    if (submenu.firstElementChild) this.sheetObserver.unobserve(submenu.firstElementChild);
    // When switching straight between mega menus, openSubmenu fades to the incoming
    // scheme; resetting here would flash the default one.
    if (this.originalColorScheme && !this.hasAttribute('data-switching')) {
      this.returnColorScheme.forEach((el) => {
        el.setAttribute('data-color-scheme', this.originalColorScheme);
      });
      this.returnColorScheme = [];
    }

    const fade = submenu.querySelectorAll('[data-animate-fade]');
    fade.forEach((element) => element.classList.add('faded-out'));
    if (preventFocus === true) return;
    trigger.focus();
  }

  openSubmenu(submenu) {
    setTimeout(() => {
      this.timeOpened = Date.now();
      const submenuType = submenu.getAttribute('data-submenu');
      this.setAttribute('open', submenuType);
      const trigger = this.querySelector(`[aria-controls="${submenu.getAttribute('id')}"]`);
      trigger.setAttribute('aria-expanded', true);
      if (this.getAttribute('data-layout') === 'vertical') {
        const height = Math.max(submenu.clientHeight, this.clientHeight, this.submenuHeight || 0);
        if (submenuType != 'megamenu') {
          this.setSubmenuHeight(height);
        }
        this.megamenuObserver.observe(submenu);
        this.classList.add('header--background');
      }
      if (submenuType === 'megamenu') {
        if (this.getAttribute('data-layout') === 'horizontal' && submenu.firstElementChild) {
          // Fires immediately on observe, which sets the sheet height.
          this.sheetObserver.observe(submenu.firstElementChild);
        }
        if (submenu.hasAttribute('data-alt-logo')) {
          this.classList.add('header--alt-logo');
        }
        const style = submenu.getAttribute('data-color-scheme-style');
        if (this.getAttribute('data-layout') === 'horizontal' && style === 'fade') {
          this.originalColorScheme = submenu.getAttribute('data-color-scheme-original');
          const newScheme = submenu.getAttribute('data-color-scheme-transition');
          this.returnColorScheme = [this, submenu];
          this.returnColorScheme.forEach((el) => {
            el.setAttribute('data-color-scheme', newScheme);
          });
        }
        if (this.getAttribute('data-layout') === 'vertical') {
          this.elements.secondaryMenu.classList.add('header__right--inactive');
          this.originalColorScheme = submenu.getAttribute('data-color-scheme-original');
          if (style === 'fade') {
            const newScheme = submenu.getAttribute('data-color-scheme-transition');
            this.returnColorScheme = [this, this.elements.background];
            this.returnColorScheme.forEach((el) => {
              el.setAttribute('data-color-scheme', newScheme);
            });
          }
          this.classList.add('header--background');
        }

        const fade = submenu.querySelectorAll('[data-animate-fade]');
        fade.forEach((element) => element.classList.remove('faded-out'));
      }
    }, 0);
  }

  // Mega menu blocks render after the bar; moving each next to its trigger gives inline
  // sibling CSS and keyboard order. Must run before the element collections are built.
  placeMegamenus() {
    this.querySelectorAll('[data-menu-trigger][data-link-handle]').forEach((trigger) => {
      const panel = this.querySelector(
        `[data-megamenu-blocks] [data-submenu="megamenu"][data-menu-handle="${trigger.dataset.linkHandle}"]`
      );
      if (!panel) return;
      trigger.parentElement.querySelector(':scope > [data-submenu="dropdown"]')?.remove();
      trigger.after(panel);
      trigger.setAttribute('aria-controls', panel.id);
    });
  }

  setSubmenuHeight(height) {
    this.submenuHeight = height;
    document.documentElement.style.setProperty('--submenu-height', height + 'px');
  }

  onSubmenuKeyDown(event) {
    switch (event.code.toUpperCase()) {
      case 'ESCAPE':
        const submenu = event.target.closest('[data-submenu]');
        this.closeSubmenu(submenu);
    }
  }

  onSubmenuFocusOut(event, submenu) {
    if (event.relatedTarget) {
      // Prevents some double activation issues
      const id = event.relatedTarget.getAttribute('aria-controls');
      if (id) {
        const el = document.getElementById(id);
        if (el.contains(submenu)) {
          return;
        }
      }
    }
    if (!submenu.contains(event.target) || !submenu.contains(event.relatedTarget)) {
      this.closeSubmenu(submenu, true);
    }
  }

  toggleTrigger(event) {
    // currentTarget: event.target can be a span inside the button.
    const trigger = event.currentTarget;
    const submenu = document.getElementById(trigger.getAttribute('aria-controls'));
    if (submenu) this.flagIfSwitching(submenu);
    if (trigger.getAttribute('aria-expanded') === 'true') {
      this.closeSubmenu(submenu);
    } else {
      this.openSubmenu(submenu);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-sticky' && newValue !== 'none') {
      window.addEventListener('scroll', this.onScroll.bind(this), false);
    }
  }

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
    if (this.getAttribute('open')) return;

    this.classList.toggle('header--filled', scrollTop > this.clientHeight);

    if (this.dataset.sticky === 'scroll') {
      if (scrollTop > this.currentScrollTop && scrollTop > this.clientHeight) {
        this.classList.add('header--scrolled-past');
        this.classList.remove('header--revealed');
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.clientHeight) {
        this.classList.add('header--scrolled-past');
        if (!this.preventReveal) {
          this.classList.add('header--revealed');
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 75);

          this.classList.remove('header--revealed');
        }
      } else if (scrollTop <= this.clientHeight) {
        this.classList.remove('header--scrolled-past');
        this.classList.remove('header--revealed');
      }
    }
    this.currentScrollTop = scrollTop;
  }
}

customElements.define('header-menu', HeaderMenu);
